import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { localISODate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };

// Everything the client screens ("Dziś" and "Panel") need, loaded once.
export const useClientData = () => {
  const [data, setData] = useState({ profile: null, plans: [], monitoring: [], packages: [], sessions: [], workouts: [] });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const profile = (await axios.get(`${API_URL}/api/client/my-profile`, opts)).data;
      const [plans, monitoring, packages, sessions, workouts] = await Promise.all([
        axios.get(`${API_URL}/api/client/my-plans`, opts),
        axios.get(`${API_URL}/api/client/my-monitoring?days=30`, opts),
        axios.get(`${API_URL}/api/packages/client/${profile.id}`, opts),
        axios.get(`${API_URL}/api/sessions/client/${profile.id}`, opts),
        axios.get(`${API_URL}/api/client/my-workout-logs?limit=30`, opts),
      ]);
      setData({
        profile,
        plans: plans.data,
        monitoring: monitoring.data,
        packages: packages.data,
        sessions: sessions.data,
        workouts: workouts.data,
      });
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // The installed app has no refresh button: reload when the client comes back after a minute or more.
  useEffect(() => {
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.hidden) hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > 60000) reload();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [reload]);

  return { ...data, loading, reload };
};

export const todayISO = () => localISODate();

export const activePlanOf = (plans) => plans.find((p) => p.status === 'active') || null;

export const dayLabel = (plan, day) => {
  const ex = plan.exercises.find((e) => e.day_number === day && e.day_label);
  return ex ? ex.day_label : `Dzień ${day}`;
};

// The next workout day of the active plan: the one after the last logged day, wrapping around.
export const nextWorkoutDay = (plan, workouts) => {
  const days = [...new Set(plan.exercises.map((e) => e.day_number))].sort((a, b) => a - b);
  if (days.length === 0) return null;
  const last = workouts
    .filter((w) => w.plan_id === plan.id)
    .sort((a, b) => (b.log_date + b.created_at).localeCompare(a.log_date + a.created_at))[0];
  if (!last) return days[0];
  const idx = days.indexOf(last.day_number);
  return days[(idx + 1) % days.length];
};

export const activePackageOf = (packages) => packages
  .filter((p) => p.status === 'active')
  .sort((a, b) => b.purchased_date.localeCompare(a.purchased_date))[0] || null;

export const MOODS = ['Źle', 'Słabo', 'OK', 'Dobrze', 'Super'];
export const moodToValue = (i) => (i + 1) * 2; // stored on the 0-10 scale used by monitoring
export const moodLabel = (v) => (v == null ? null : MOODS[Math.min(4, Math.max(0, Math.round(v / 2) - 1))]);

export const formatDate = (iso, options = { day: 'numeric', month: 'short' }) =>
  new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('pl-PL', options);
